import {useEffect, useState} from 'react'
import {Button, Col, Form, Row} from 'react-bootstrap'
import Select from 'react-select'
import {TFormInfoContact, TFormMemberPolicy} from '../policiesTypes'
import {useLazyQuery} from '@apollo/client'
import {GET_ALL_CONTACTS_PREVIEW} from 'app/tfi/gql/queries/contactsApiQueries'
import {toast} from 'react-toastify'
import {useDebounce} from '../../../../../_metronic/helpers'
import {calculateAge} from '../../../helpers'
import {FormikErrors, FormikTouched} from 'formik'
import { GENDER_OPTIONS } from '../../../helpers/consts'
import { DateInput } from 'app/tfi/components/DateInput'
import { PhoneInput } from 'app/tfi/components/PhoneInput'
import {IResContactsPreviewAPI, NodeContact} from 'app/tfi/types/contactsApi'

type ContactOption = {
  label: string
  value: string
  contact: NodeContact
}

type PolicyOwnershipValues = {
  ownsCar?: boolean | null
  ownsHome?: boolean | null
}

export const PolicyHolderTabModal: React.FC<{
  onPrevStep?: () => void
  onNextStep: () => void
  handleChange: (field: string, value: any) => void
  handleBlur: (field: string) => void
  values: TFormMemberPolicy | TFormInfoContact
  errors: FormikErrors<TFormMemberPolicy | TFormInfoContact>
  touched: FormikTouched<TFormMemberPolicy | TFormInfoContact>
  policyValues?: PolicyOwnershipValues
  policyErrors?: FormikErrors<any>
  policyTouched?: FormikTouched<any>
  onPolicyChange?: (field: keyof PolicyOwnershipValues, value: boolean | null) => void
  onPolicyBlur?: (field: keyof PolicyOwnershipValues) => void
  submitCount?: number
  isContactModule?: boolean
  showSearchContact?: boolean
}> = ({
  onNextStep,
  onPrevStep,
  handleBlur,
  handleChange,
  values,
  errors,
  touched,
  policyValues,
  policyErrors,
  policyTouched,
  onPolicyChange,
  onPolicyBlur,
  submitCount,
  isContactModule,
  showSearchContact,
}) => {
  const [gqlSearchContacts, {data: dataContacts, loading, error}] =
    useLazyQuery<IResContactsPreviewAPI>(GET_ALL_CONTACTS_PREVIEW, {
      fetchPolicy: 'no-cache',
    })
  const [inputSelect, setInputSelect] = useState('')
  const valueDeb = useDebounce(inputSelect, 500)
  const [options, setOptions] = useState<ContactOption[]>([])
  const [selectedContact, setSelectedContact] = useState<ContactOption | null>(null)
  const [tab, setTab] = useState<'new' | 'existing'>('new')
  const [holderApplicants, setHolderApplicants] = useState<string>('yes')

  useEffect(() => {
    if (tab === 'new') {
      setSelectedContact(null)
      setInputSelect('')
    }
  }, [tab])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (!dataContacts?.allContactsPreview?.edges) return
    const mapped = dataContacts.allContactsPreview.edges.map(({node}) => {
      const name =
        node.fullName ||
        `${node.firstName || ''} ${node.lastName || ''}`.replace(/\s+/g, ' ').trim()
      const phone = node.phone ? ` - ${node.phone}` : ''
      return {
        label: `${name}${phone}`,
        value: node.id,
        contact: node,
      }
    })
    setOptions(mapped)
  }, [dataContacts])

  useEffect(() => {
    if (tab !== 'existing') return
    if (!valueDeb || valueDeb.trim().length < 4) return
    gqlSearchContacts({
      variables: {
        name: valueDeb.trim(),
      },
    })
  }, [gqlSearchContacts, tab, valueDeb])

  const applyContactToForm = (contact: NodeContact) => {
    const fallback = (val?: string | null) => val ?? ''

    if ('id' in values) handleChange('id', fallback(contact.id))
    if ('firstName' in values) handleChange('firstName', fallback(contact.firstName))
    if ('middleName' in values) handleChange('middleName', fallback(contact.middleName))
    if ('lastName' in values) handleChange('lastName', fallback(contact.lastName))
    if ('secondLastName' in values) handleChange('secondLastName', fallback(contact.secondLastName))
    if ('preferredLenguage' in values) handleChange('preferredLenguage', fallback(contact.preferredLenguage))

    if ('dateBirth' in values) handleChange('dateBirth', fallback(contact.dateBirth || ''))
    if ('gender' in values) handleChange('gender', fallback(contact.gender))

    if ('phone' in values) handleChange('phone', fallback(contact.phone))

    if (isContactModule) {
      if ('email' in values) handleChange('email', fallback(contact.email))
      if ('fullName' in values) handleChange('fullName', fallback(contact.fullName))
      if ('stateEUId' in values) handleChange('stateEUId', fallback(contact.stateEU?.id))
      if ('zipCode' in values) handleChange('zipCode', fallback(contact.zipCode))
      if ('city' in values) handleChange('city', fallback(contact.city))
      if ('address1' in values) handleChange('address1', fallback(contact.address1))
      if ('address2' in values) handleChange('address2', fallback(contact.address2))
      if ('immigrationStatus' in values) handleChange('immigrationStatus', fallback(contact.immigrationStatus))
    } else {
      if ('emailAddress' in values) handleChange('emailAddress', fallback(contact.email))
    }
  }

  const handleSelectContact = (option: ContactOption | null) => {
    setSelectedContact(option)
    if (!option) return
    applyContactToForm(option.contact)
  }

  const showError = (field: string) => (touched[field] || (submitCount ?? 0) > 0) && errors[field]

  const showPolicyError = (field: keyof PolicyOwnershipValues) =>
    (((policyTouched as any)?.[field] || (submitCount ?? 0) > 0) &&
      (policyErrors as any)?.[field]) ||
    null

  const toSelectValue = (value?: boolean | null) => {
    if (value === true) return 'true'
    if (value === false) return 'false'
    return ''
  }

  const handlePolicySelect = (field: keyof PolicyOwnershipValues, value: string) => {
    if (!onPolicyChange) return
    const parsed = value === '' ? null : value === 'true'
    onPolicyChange(field, parsed)
  }

  // console.log('showSearchContact:' + showSearchContact)

  return (
    <div className='contact-information'>
      {showSearchContact && (
        <div className='search-contact-policy-form'>
          <Button onClick={() => setTab(tab === 'new' ? 'existing' : 'new')} variant='primary'>
            {tab === 'new' ? (
              <i className='bi bi-person-check' />
            ) : (
              <i className='bi bi-person-plus' />
            )}
            {tab === 'new' ? 'EXISTING CONTACT' : 'NEW CONTACT'}
          </Button>

          {tab === 'existing' && (
            <div className='existing-contact'>
              <Form.Group>
                <Form.Label>Find and select a contact</Form.Label>
                <Select
                  isLoading={loading}
                  isClearable
                  loadingMessage={() => 'Loading contacts...'}
                  noOptionsMessage={() =>
                    inputSelect.trim().length < 4
                      ? 'Type at least 4 characters to search'
                      : 'No contacts found'
                  }
                  placeholder='Search and select a contact...'
                  filterOption={() => true}
                  options={options}
                  value={selectedContact}
                  inputValue={inputSelect}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      const query = inputSelect.trim()
                      if (query.length >= 4) {
                        gqlSearchContacts({
                          variables: {
                            name: query,
                          },
                        })
                      }
                    }
                  }}
                  onInputChange={(value, meta) => {
                    if (meta.action === 'input-change') {
                      setInputSelect(value)
                    } else if (meta.action === 'set-value') {
                      setInputSelect('')
                    }
                  }}
                  onChange={(option) => handleSelectContact(option as ContactOption | null)}
                />
              </Form.Group>
              <Form.Text style={{fontSize: '12px'}}>
                Search by name, email address, phone number or last 4 digits of SSN
              </Form.Text>
            </div>
          )}
        </div>
      )}

      <div className='title-section-policy-form'>
        {isContactModule ? <h6>Contact Information </h6> : <h6>Policyholder Information </h6>}
      </div>
      <div>
        <Row>
          {/* <Col md={6}>
            <Form.Group>
              <Form.Label>
                <label htmlFor='Preferredlanguage'>Preferred language</label>
              </Form.Label>
              <Form.Select
                name='Preferredlanguage'
                // onChange={(e) => onChange(e.target, 'policyholder')}
              >
                <option value='yes'>Spanish</option>
                <option value='no'>English</option>
              </Form.Select>
            </Form.Group>
          </Col> */}
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                First Name{' '}
                <small className='text-danger'>
                  {'firstName' in values &&
                    showError('firstName') &&
                    typeof errors.firstName === 'string' && <>{errors.firstName}</>}
                </small>
              </Form.Label>
              <Form.Control
                placeholder='Enter first name'
                onChange={(e) => handleChange('firstName', e.target.value)}
                value={values.firstName ?? ''}
                onBlur={() => handleBlur('firstName')}
                className={touched.firstName && errors.firstName ? 'input-error' : ''}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Middle Name</Form.Label>
              <Form.Control
                placeholder='Enter middle name'
                onChange={(e) => handleChange('middleName', e.target.value)}
                onBlur={() => handleBlur('middleName')}
                value={values.middleName ?? ''}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                Last Name{' '}
                <small className='text-danger'>
                  {'lastName' in values &&
                    touched.lastName &&
                    typeof errors.lastName === 'string' && <>{errors.lastName}</>}
                </small>
              </Form.Label>
              <Form.Control
                placeholder='Enter last name'
                onChange={(e) => handleChange('lastName', e.target.value)}
                onBlur={() => handleBlur('lastName')}
                value={values.lastName ?? ''}
                className={touched.lastName && errors.lastName ? 'input-error' : ''}
                required
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Second Last Name</Form.Label>
              <Form.Control
                placeholder='Enter second last name'
                onChange={(e) => handleChange('secondLastName', e.target.value)}
                value={values.secondLastName ?? ''}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <DateInput
                id="dateBirth"
                name="dateBirth"
                label="Date of Birth"
                value={values.dateBirth}
                error={errors.dateBirth as string}
                touched={touched.dateBirth as boolean}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {values.dateBirth && (
                <div className="label-dob">
                  <span className="label">Age: </span>
                  <span>{calculateAge(values.dateBirth)}</span>
                  <span> years old</span>
                </div>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                Gender{' '}
                <small className='text-danger'>
                  {'gender' in values && touched.gender && typeof errors.gender === 'string' && (
                    <>{errors.gender}</>
                  )}
                </small>
              </Form.Label>
              <Form.Select
                onChange={(e) => handleChange('gender', e.target.value)}
                onBlur={() => handleBlur('gender')}
                value={values.gender ?? ''}
                className={touched.gender && errors.gender ? 'input-error' : ''}
                required
              >
                <option value=''>Select...</option>
                {GENDER_OPTIONS.map(({value, label}) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <PhoneInput
              id="phone"
              name="phone"
              label="Phone"
              value={values.phone}
              error={errors.phone as string}
              touched={touched.phone as boolean}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                Email{' '}
                {isContactModule ? (
                  <small className='text-danger'>
                    {'email' in values && touched.email && typeof errors.email === 'string' && (
                      <>{errors.email}</>
                    )}
                  </small>
                ) : (
                  <small className='text-danger'>
                    {'emailAddress' in values &&
                      touched.emailAddress &&
                      typeof errors.emailAddress === 'string' && <>{errors.emailAddress}</>}
                  </small>
                )}
              </Form.Label>
              {isContactModule ? (
                <Form.Control
                  type='email'
                  placeholder='Enter email'
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={() => handleBlur('email')}
                  value={values.email ?? ''}
                  className={touched.email && errors.email ? 'input-error' : ''}
                  required
                />
              ) : (
                <Form.Control
                  type='email'
                  placeholder='Enter email'
                  onChange={(e) => handleChange('emailAddress', e.target.value)}
                  onBlur={() => handleBlur('emailAddress')}
                  value={values.emailAddress ?? ''}
                  className={touched.emailAddress && errors.emailAddress ? 'input-error' : ''}
                  required
                />
              )}
            </Form.Group>
          </Col>
        </Row>
        {!isContactModule && (
          <Row className='mt-2'>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Owns car <small className='text-danger'>{showPolicyError('ownsCar')}</small>
                </Form.Label>
                <Form.Select
                  value={toSelectValue(policyValues?.ownsCar)}
                  onChange={(e) => handlePolicySelect('ownsCar', e.target.value)}
                  onBlur={() => onPolicyBlur?.('ownsCar')}
                  required
                >
                  <option value=''>Select...</option>
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Owns home <small className='text-danger'>{showPolicyError('ownsHome')}</small>
                </Form.Label>
                <Form.Select
                  value={toSelectValue(policyValues?.ownsHome)}
                  onChange={(e) => handlePolicySelect('ownsHome', e.target.value)}
                  onBlur={() => onPolicyBlur?.('ownsHome')}
                  required
                >
                  <option value=''>Select...</option>
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        )}
        {!isContactModule && (
          <Row className='mt-2'>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Is this member an applicant?</Form.Label>
                <Form.Select
                  value={holderApplicants}
                  onChange={(e) => setHolderApplicants(e.target.value)}
                >
                  <option value='yes'>Yes</option>
                  <option value='no'>No</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        )}
      </div>
      <div className='btn-prev-next'>
        <div></div>
        <Button size='sm' onClick={onNextStep}>
          NEXT STEP
        </Button>
      </div>
    </div>
  )
}
